import { defineComponent as _defineComponent } from 'vue'

import { ApiPostLoadStatus, ApiPostLoadStatusType } from "@/modules/types/apiLoadStatusType";
import { PropType, reactive, toRefs, inject } from "vue";
import ApiPostLoadMaster from "@/components/common/api/ApiPostLoadMaster.vue";
import MediaController from "@/controllers/communityTalk/mediaController";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import CustomLabel from "./CustomLabel.vue";
import MediaUtil from "@/modules/utils/mediaUtil";
import { PostMediaViewModel } from "@/viewModels/communityTalk/postMediaViewModel";

interface State {
	/**
	 * inputHTMLの内容。
	 */
	inputElement: HTMLInputElement | null;
	/**
	 * 画像・動画投稿APIステータス。
	 */
	status: ApiPostLoadStatusType;
	/**
	 * 画像・動画投稿APIエラーメッセージ。
	 */
	errorMessage: string;
	/**
	 * フォーム多重送信制御用フラグ
	 */
	isProcessing: boolean;
	/**
	 * 画像・動画投稿キャンセルフラグ。
	 */
	isCanceledUpload: boolean;
	/**
	 * 親コンポーネントからのAPIエラーメッセージ。
	 */
	parentErrorMessage: string;
	/**
	 * 画像・動画投稿のプレビュー用ViewModel。
	 */
	postMediaViewModels: PostMediaViewModel[];
}
// reactiveな変数宣言
interface Emits {
	// 画像・動画アップロード完了イベント
	(e: "on-success-upload-medias", v: PostMediaViewModel[]): void;
	// 画像・動画の選択を解除したイベント
	(e: "on-reset-medias"): void;
	// 画像・動画の投稿が開始されたイベント
	(e: "on-start-post-process"): void;
	// 画像・動画の投稿が完了したイベント
	(e: "on-end-post-process"): void;
}

export default /*#__PURE__*/_defineComponent({
  props: {
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: {
		type: String,
		required: true,
		default: "",
	},
	/**
	 * 投稿種類。
	 * トークルームとして: `TalkRoom`
	 * リプライとして: `Reply`
	 */
	postType: {
		type: String,
		required: true,
		default: "TalkRoom",
		validator(val: string) {
			return ["TalkRoom", "Reply"].includes(val);
		},
	},
	/**
	 * 入力欄を無効化するかどうか。
	 */
	disabled: {
		type: Boolean,
		required: false,
		default: false,
	},
	/**
	 * 表示時の初期値。
	 */
	initPostMediaViewModels: {
		type: Object as PropType<PostMediaViewModel[]>,
		required: true,
	},
},
  emits: ["on-success-upload-medias", "on-reset-medias", "on-start-post-process", "on-end-post-process"],
  setup(__props, { expose, emit: emits }: { emit: ({
	// 画像・動画アップロード完了イベント
	(e: "on-success-upload-medias", v: PostMediaViewModel[]): void;
	// 画像・動画の選択を解除したイベント
	(e: "on-reset-medias"): void;
	// 画像・動画の投稿が開始されたイベント
	(e: "on-start-post-process"): void;
	// 画像・動画の投稿が完了したイベント
	(e: "on-end-post-process"): void;
}), expose: any, slots: any, attrs: any }) {
  expose()

const props = __props
/**
 * 画像・動画投稿コンポーネント。
 * APIへリクエスト送付も行い、Storeへ投稿された画像・動画IDを保持する。
 * バリデーションエラー表示にも対応。
 */

const mediaController = new MediaController();



const name = props.postType === "TalkRoom" ? "ProfilePicId" : "reply.ProfilePicId";

const state = reactive<State>({
	inputElement: null,
	status: ApiPostLoadStatus.beforeRequest,
	errorMessage: "",
	isProcessing: false,
	isCanceledUpload: false,
	parentErrorMessage: "",
	postMediaViewModels: props.initPostMediaViewModels,
});
const { inputElement, status, errorMessage, isProcessing, isCanceledUpload, parentErrorMessage } = toRefs(state);

// Emits


// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

// ファイル選択ボタン押下時の処理
const onClickInputFileButton = (): void => {
	if (state.inputElement) {
		state.inputElement.click();
	}
};

// 画像・動画をアップロード
const upload = async (): Promise<void> => {
	// ファイルが存在しなければ処理を空振り
	if (!state.inputElement) return;
	if (!state.inputElement.files) return;

	state.isProcessing = true;
	state.isCanceledUpload = false;
	state.status = ApiPostLoadStatus.loading;
	emits("on-start-post-process");

	try {
		const fileList = state.inputElement.files;
		const files = Array.from(fileList);

		// 画像・動画ファイルのバリデーションチェック
		const validErrorMessages = await mediaController.checkPictureAndMovie(files);
		if (validErrorMessages.length !== 0) {
			state.errorMessage = validErrorMessages.join("\n");
			return;
		}

		// 投稿がキャンセルされてなければ、対象画像・動画投稿を作成&親コンポーネントに通知
		if (!state.isCanceledUpload) {
			const viewModels = await mediaController.createMedias(files, communityTalkStore.getTenantId());
			state.postMediaViewModels = viewModels;
			emits("on-success-upload-medias", viewModels);
		}
		state.errorMessage = "";
	} catch (e) {
		const otherErrorMessage = "写真・動画のアップロードに失敗しました。";
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if (e && (e as any).message) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			state.errorMessage = (e as any).message;
		} else {
			// エラー内容が多岐にわたるため、一律でエラーメッセージを統一
			state.errorMessage = otherErrorMessage;
		}
	} finally {
		// 成功しても失敗しても、再度画像を投稿するようにしたいのでAPIステータスは投稿前のステータスへ
		state.status = ApiPostLoadStatus.beforeRequest;
		state.isProcessing = false;
		emits("on-end-post-process");
		// 投稿がキャンセルされている場合、親コンポーネントに通知
		if (state.isCanceledUpload) {
			state.postMediaViewModels = [];
			emits("on-reset-medias");
		}
		state.isCanceledUpload = false;
	}
};

// 画像・動画のアップロードキャンセル処理
const cancelUpload = (): void => {
	state.isCanceledUpload = true;
	state.errorMessage = "";
	state.status = ApiPostLoadStatus.beforeRequest;
};

// 画像・動画の登録解除
const detachMedia = (): void => {
	// inputタグ内部のファイルデータを削除
	if (!state.inputElement) return;
	state.inputElement.value = "";
	// 登録解除を親コンポーネントに通知
	state.postMediaViewModels = [];
	emits("on-reset-medias");
};

// 画像・動画のプレビューエリアのClass
const previewMediaClass = (viewModel: PostMediaViewModel): string => {
	const baseClassName = "revico-community-talk-post-media-preview--";
	switch (viewModel.type) {
		case "Image":
			return `${baseClassName}img`;
		case "Movie":
			return `${baseClassName}thumbnail`;
		default:
			throw new Error(`想定外のtypeが渡されました。type: ${viewModel.type}`);
	}
};

const __returned__ = { mediaController, props, name, state, inputElement, status, errorMessage, isProcessing, isCanceledUpload, parentErrorMessage, emits, communityTalkStore, onClickInputFileButton, upload, cancelUpload, detachMedia, previewMediaClass, ApiPostLoadMaster, CustomLabel, MediaUtil }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})